import { ReactNode, useEffect, useState } from "react";
import { JSX } from "react";
import ReactGA from "react-ga4";

import { baseTheme } from "@4pace-cpq/core";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { GoogleReCaptchaProvider } from "@google-recaptcha/react";
import { Theme, ThemeProvider, createTheme } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
import { GatewayProvider } from "@plansoft-configuration-renderer/gateway";

import {
  EnvJSONProvider,
  useEnvJSON,
} from "./configurator/renderer/customContext/EnvContext";
import { GlobalErrorNotifier } from "./errorHandling/GlobalErrorNotifier";
import { MaintenanceModeOverlay } from "./errorHandling/MaintenanceModeOverlay";
import { QueryErrorBridge } from "./errorHandling/QueryErrorBridge";
import { OnboardingGate } from "./onboarding/OnboardingGate";
import { AdministrationAreaProvider } from "./providers/administrationAreaProvider";
import { AppSettingsProvider } from "./providers/appSettingsProvider";
import { EasySessionProvider } from "./providers/easySessionProvider";
import { UiTranslationsProvider } from "./providers/uiTranslationsProvider";
import { theme as WEGTheme } from "./theme/theme";

const cacheNoPrefixer = createCache({
  //removes the vendor prefix on dev
  key: "noprefixer",
  ...(process.env.NODE_ENV === "development" && { stylisPlugins: [] }),
});
const theme = createTheme(deepmerge(baseTheme as Theme, WEGTheme));

/**
 * Everything below needs `envJSON` to be loaded before it can render, so it
 * stays gated behind `EnvJSONProvider` until the fetch resolves. Google
 * Analytics is initialized here too, once - other components (e.g.
 * `CustomCartIdRouteComponent`) call `ReactGA.send`/`ReactGA.event`, and this
 * gate ensures they never mount before `ReactGA.initialize` has run.
 */
function ProvidersInner({ children }: { children: ReactNode }): ReactNode {
  const { envJSON, isLoadingEnv } = useEnvJSON();
  const [isGaInitialized, setIsGaInitialized] = useState(false);

  useEffect(() => {
    if (envJSON) {
      if (envJSON.APP_KEY_GA) {
        ReactGA.initialize(envJSON.APP_KEY_GA);
      } else {
        console.warn(
          "env.json is missing APP_KEY_GA - skipping Google Analytics initialization",
        );
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect -- run once GA is configured (or skipped), gates children below from mounting (and sending events) before ReactGA.initialize has run
      setIsGaInitialized(true);
    }
  }, [envJSON]);

  if (isLoadingEnv || !envJSON || !isGaInitialized) {
    return null;
  }

  return (
    <GoogleReCaptchaProvider type="v3" siteKey={envJSON.APP_KEY_RECAPTCHA}>
      {/* EasySessionProvider must wrap AdministrationAreaProvider: its first
          CheckLoginState establishes the session cookie, and session-scoped
          requests (getQuoteId, admin area) wait for it - see useEnsureQuoteExists */}
      <EasySessionProvider>
        <AdministrationAreaProvider>
          <AppSettingsProvider>
            <UiTranslationsProvider>
              <QueryErrorBridge />
              <GlobalErrorNotifier />
              <OnboardingGate />
              {children}
            </UiTranslationsProvider>
          </AppSettingsProvider>
        </AdministrationAreaProvider>
      </EasySessionProvider>
    </GoogleReCaptchaProvider>
  );
}

export function Providers({ children }: { children: ReactNode }): JSX.Element {
  return (
    <GatewayProvider>
      <EnvJSONProvider>
        <CacheProvider value={cacheNoPrefixer}>
          <ThemeProvider theme={theme}>
            <MaintenanceModeOverlay />
            <ProvidersInner>{children}</ProvidersInner>
          </ThemeProvider>
        </CacheProvider>
      </EnvJSONProvider>
    </GatewayProvider>
  );
}
